import { forwardRef, memo, useState } from "react";

import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import {
  EmbedTemplate,
  EmbedTheme,
} from "@hl/shared-features/lib/features/mint/types";
import {
  Box,
  Button,
  Group,
  List,
  Modal,
  Select,
  Stack,
  Tabs,
  Text,
  Title,
  createStyles,
} from "@mantine/core";
import { Prism } from "@mantine/prism";

import { ReactComponent as CopyEmbedIcon } from "assets/icons/copy-fill.svg";
import { MINT_CDN_URL } from "~/config";

import { EmbedPreview } from "./EmbedPreview";
import { EmbedThemeEditor } from "./ThemeEditor";

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const useStyles = createStyles((theme) => ({
  preview: {
    borderLeft: `1px solid ${theme.colors.divider[0]}`,
  },
}));

type Props = {
  opened: boolean;
  onClose: () => void;
  collectionId: string;
  entity: "mint" | "auction";
};
const EmbedSaleModal = ({ opened, onClose, collectionId, entity }: Props) => {
  const { classes } = useStyles();
  const [copied, setCopied] = useState<boolean>(false);
  const embedShowModal = useFeatureFlags(FEATURE_FLAGS.EMBEDS_SHOW_MODAL);

  const [theme, setTheme] = useState<EmbedTheme | null>(null);

  const themeProp = theme ? `data-theme='${JSON.stringify(theme)}'` : "";
  const [template, setTemplate] = useState<string | null>("default");

  const templateProp =
    template && template !== "default" ? `data-template='${template}'` : "";

  const stylesheetSourceUrl = () => {
    return window.location.host.includes("localhost")
      ? "http://127.0.0.1:8081/assets/embed.css"
      : `${MINT_CDN_URL}/assets/embed.css`;
  };
  const moduleSourceUrl = () => {
    return window.location.host.includes("localhost")
      ? "http://127.0.0.1:8081/assets/embed.js?v=1"
      : `${MINT_CDN_URL}/assets/embed.js?v=1`;
  };
  const pageEmbedCodeSnippet = `
<link rel="stylesheet" href="${stylesheetSourceUrl()}" />
<div data-widget="highlight-mint-page" data-mint-collection-id="${collectionId}" ></div>
<script type="module" crossorigin="true" src="${moduleSourceUrl()}"></script>
  `;

  const cardEmbedCodeSnippet = `
<link rel="stylesheet" href="${stylesheetSourceUrl()}" />
<div data-widget="highlight-mint-card" data-mint-collection-id="${collectionId}" ${themeProp} ${templateProp}></div>
<script type="module" crossorigin="true" src="${moduleSourceUrl()}"></script>
  `;

  const handleCopyPageEmbedCode = () => {
    navigator.clipboard.writeText(pageEmbedCodeSnippet);
    setCopied(true);
  };

  const handleCopyCardEmbedCode = () => {
    navigator.clipboard.writeText(cardEmbedCodeSnippet);
    setCopied(true);
  };

  if (!embedShowModal) {
    return <></>;
  }
  const entityCap = capitalize(entity);

  return (
    <Tabs
      variant="pills"
      defaultValue="card"
      mt="lg"
      keepMounted={false}
      styles={{
        tab: {
          paddingLeft: 12,
          paddingRight: 12,
        },
        panel: { paddingTop: 0 },
      }}
    >
      <Modal
        title={
          <Stack>
            <Title size={18}>Embed {entityCap}</Title>
            <Tabs.List mt="sm">
              <Tabs.Tab value="card">{entityCap} card</Tabs.Tab>
              <Tabs.Tab value="page">{entityCap} page</Tabs.Tab>
            </Tabs.List>
          </Stack>
        }
        size="lg"
        opened={opened}
        onClose={onClose}
        styles={{
          modal: {
            width: 1000,
          },
          header: {
            marginBottom: 0,
          },
        }}
      >
        <Tabs.Panel value="card" pb="sm">
          <Group position="apart" align="center" noWrap spacing={0}>
            <Box w="50%" pr={40}>
              <Instructions entity={entity} method="card" />
              <TemplateSelect value={template} onChange={setTemplate} />
              <EmbedThemeEditor onThemeChange={setTheme} />
              <Stack mt="lg">
                <Text size="md" component="label">
                  Embed code
                </Text>
                <Prism colorScheme="dark" language="javascript">
                  {cardEmbedCodeSnippet}
                </Prism>
                <Button
                  color={SECONDARY_COLOR}
                  size="xl"
                  fullWidth
                  leftIcon={<CopyEmbedIcon />}
                  onClick={() => handleCopyCardEmbedCode()}
                >
                  {copied ? "Copied!" : "Copy embed code"}
                </Button>
              </Stack>
            </Box>
            <Box w="50%" className={classes.preview}>
              <EmbedPreview code={cardEmbedCodeSnippet} />
            </Box>
          </Group>
        </Tabs.Panel>

        <Tabs.Panel value="page" py="sm">
          <Instructions entity={entity} method="page" />
          <Stack mt="lg">
            <Text size="md" weight={500}>
              Embed code
            </Text>
            <Prism colorScheme="dark" language="javascript">
              {pageEmbedCodeSnippet}
            </Prism>
            <Button
              color={SECONDARY_COLOR}
              size="xl"
              fullWidth
              leftIcon={<CopyEmbedIcon />}
              onClick={() => handleCopyPageEmbedCode()}
            >
              {copied ? "Copied!" : "Copy embed code"}
            </Button>
          </Stack>
        </Tabs.Panel>
      </Modal>
    </Tabs>
  );
};

interface TemplateOptionProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  notes: string;
}

const TemplateOption = forwardRef<HTMLDivElement, TemplateOptionProps>(
  ({ label, notes, ...others }: TemplateOptionProps, ref) => (
    <div ref={ref} {...others}>
      <Stack spacing="xs">
        <Text size="md" weight={500}>
          {label}
        </Text>
        <Text size="sm" color={TEXT_COLOR.SECONDARY}>
          {notes}
        </Text>
      </Stack>
    </div>
  )
);

const TemplateSelect = ({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
}) => (
  <Select
    value={value}
    onChange={onChange}
    mt="lg"
    label="Mint card options"
    data={[
      {
        value: EmbedTemplate.Default,
        label: "Default",
        notes: "Display art with mint details",
      },
      {
        value: EmbedTemplate.NoArt,
        label: "Without art",
        notes: "Only show mint details, hide art",
      },
      {
        value: EmbedTemplate.ArtOnly,
        label: "Art only",
        notes: "Only show art",
      },
      {
        value: EmbedTemplate.Compact,
        label: "Compact",
        notes:
          "Display art and mint button, but hide additional details (title, links, etc.)",
      },
      {
        value: EmbedTemplate.ButtonOnly,
        label: "Button only",
        notes: "Only show the mint button",
      },
    ]}
    itemComponent={TemplateOption}
  />
);

const Instructions = ({
  entity,
  method,
}: {
  entity: "mint" | "auction";
  method: string;
}) => (
  <>
    <Text size="lg" weight={500} pb="md">
      How to embed your {entity} {method}
    </Text>
    <List type="ordered" spacing="sm">
      <List.Item>
        Ensure your {entity} is configured the way you want, including price,
        start date, end date, etc.
      </List.Item>
      <List.Item>Copy the embed code below</List.Item>
      <List.Item>
        Paste this code into a blank page on your website where you want the{" "}
        {entity} to appear
      </List.Item>
    </List>
  </>
);

export default memo(EmbedSaleModal);
